﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import {
    IdentifierFormat,
    OrganizationIdentifierSubTypes,
} from "~base/Internationalization/ConfigurationInfo";
import { UserInfoCustomizations } from "~base/Components/Pages/Shared/UserInfoCard";
import { JssClass } from "~base/Styling";
import * as sessionModel from "~base/Services/API/Session/Model";
import { getSize } from "~base/Components/Layout/JssSpacing";
import * as React from "react";
import { SessionCookieLink } from "~base/Components/Composite/SessionCookieLink";
import { IntlContext } from "~base/Internationalization/IntlContext";
import { StartPageStyles } from "./Styles";
import { SecureRouteLink } from "~base/Components/Pages/Shared/SecureRouteLink";

//-------------------------------------------------------------------------------------
// Customizations
//-------------------------------------------------------------------------------------

export interface StartPageCustomizations {
    individualIdentifierFormat?: IdentifierFormat;
    organizationIdentifierFormat?: IdentifierFormat;
    identifierTextCode?: string;
    hideRegistrationLink?: boolean;
    tryForPreferredUiCulture?: boolean;
    allowSelfServiceLanguageSelection?: boolean;
    hideSessionModelSelectWhenSingleChoice?: boolean;
    adminMenu?: (userRole: string) => React.ReactNode;
    userInfoCustomization?: UserInfoCustomizations;
    makeSubjectIdReadOnlyIfFromQS?: boolean;
    allowMultipleCodes?: boolean;
    customStyles?: StartPageStyles;
    filerSessionsResume: (session: sessionModel.Session) => boolean;
    identifierPlaceholder?: string;
    additionalAllowedOrganizationTypes?: Array<OrganizationIdentifierSubTypes>;
    waitForTextCodesToLoad?: boolean;
    formatSessionDisplayName?: (
        intl: IntlContext,
        sessionModel: sessionModel.Session,
        sessionModelInfo: sessionModel.GetStartInfoSessionModel
    ) => string;
    onCloseIconFQN?: string;
    versionInfo?: VersionInfoPlacement;
    requireSessionModelSelection?: (
        sessionModels: sessionModel.GetStartInfoSessionModel[]
    ) => boolean;
}

type VersionInfoPlacement = "PoweredBy" | "VersionInfoControl" | "None";

export const defaultStyling = {
    header: {
        padding: `25px ${25 + getSize("HalfMedium")}px 0px ${25 + getSize("HalfMedium")}px`,
    },
    bottom: {
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "center",
        justifyContent: "space-between",
    },
    errorText: {
        overflow: "auto",
    },
};

export const adminMenu = (role: string): React.ReactNode => (
    <div style={{ display: "flex", gap: "5px" }}>
        <SessionCookieLink
            roles={["sysadmin", "operator"]}
            userAccessViaProperty={"VisiFinance.Consilio.HangfireAccess"}
            uri="/hangfire"
        >
            Hangfire
        </SessionCookieLink>
        <SessionCookieLink
            roles={["tenantadmin", "sysadmin", "operator"]}
            uri="/swagger"
        >
            Swagger
        </SessionCookieLink>
        <SecureRouteLink
            forceLoadAsExternal
            to={"/Admin/Telemetry"}
            roles={["sysadmin", "operator"]}
        >
            Telemetry
        </SecureRouteLink>
    </div>
);

export const StartPageDefaultCustomization = {
    hideSessionModelSelectWhenSingleChoice: false,
    allowMultipleCodes: false,
    customStyles: defaultStyling,
    tryForPreferredUiCulture: false,
    allowSelfServiceLanguageSelection: false,
    filerSessionsResume: (s) => true,
    adminMenu: adminMenu,
    additionalAllowedOrganizationTypes: [],
    formatSessionDisplayName: (
        intl: IntlContext,
        session: sessionModel.Session,
        sessionModelInfo: sessionModel.GetStartInfoSessionModel
    ) => {
        const displayValue = intl.formatDateMonthTimeLong(session.creationDate);

        if (session.access == "Read") {
            return displayValue + " *";
        }

        return displayValue;
    },
    onCloseIconFQN: "Consilio.UI.Icons.Close",
    versionInfo: "VersionInfoControl",
    requireSessionModelSelection: () => false,
} as StartPageCustomizations;
